import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Image as ImageIcon, Loader2, Link } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUpload Component
 * Supports both URL input and file upload
 * 
 * Props:
 * - value: current image URL
 * - onChange: callback when image changes (receives URL string)
 * - token: auth token for upload
 * - uploadEndpoint: optional custom upload endpoint (default: /api/upload-image)
 * - placeholder: placeholder text
 * - label: label text
 */
const ImageUpload = ({ 
  value, 
  onChange, 
  token, 
  uploadEndpoint = "/api/upload-image",
  placeholder = "https://example.com/image.jpg",
  label = "Image"
}) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState(value?.startsWith("http") ? "url" : "upload");
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(
        `${BACKEND_URL}${uploadEndpoint}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.url) {
        onChange(response.data.url);
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("Failed to upload image. Please try again or use a URL.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = () => {
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium mb-1 block">{label}</label>
      
      {/* Mode Toggle */}
      <div className="flex gap-2 mb-2">
        <Button
          type="button"
          variant={mode === "upload" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("upload")}
          className="text-xs"
        >
          <Upload className="w-3 h-3 mr-1" />
          Upload
        </Button>
        <Button
          type="button"
          variant={mode === "url" ? "default" : "outline"}
          size="sm"
          onClick={() => setMode("url")}
          className="text-xs"
        >
          <Link className="w-3 h-3 mr-1" />
          URL
        </Button>
      </div>

      {mode === "url" ? (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
      ) : (
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="image-upload-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Choose Image
              </>
            )}
          </Button>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative mt-2 inline-block">
          <img
            src={value.startsWith("/") ? `${BACKEND_URL}${value}` : value}
            alt="Preview"
            className="w-32 h-24 object-cover rounded-lg border"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
            onClick={handleRemove}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
